import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Bell,
  BellOff,
  AlertTriangle,
  Clock,
  CheckCircle,
  Package,
  XCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const SNOOZE_PRESETS = [
  { hours: 1,  label: '1 hora' },
  { hours: 4,  label: '4 horas' },
  { hours: 8,  label: '8 horas' },
  { hours: 24, label: '24 horas' },
];

const NotificationCenter = () => {
  const { api, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // v5.3.7: per-user snooze
  const [snooze, setSnooze] = useState({ active: false, snooze_until: null, snooze_types: [], digest_opt_in: null });
  // v5.3.8: granular snooze type selection (transient — collected before applying)
  const [snoozeTypeFilter, setSnoozeTypeFilter] = useState([]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifRes, countRes, snoozeRes] = await Promise.all([
        api().get('/notifications?limit=20'),
        api().get('/notifications/unread-count'),
        api().get('/users/me/notification-snooze').catch(() => ({ data: { active: false, snooze_until: null } })),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
      setSnooze(snoozeRes.data || { active: false, snooze_until: null });
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [api, user]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api().post(`/notifications/${notificationId}/mark-read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      await api().post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type, priority) => {
    switch (type) {
      case 'bomberazo':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'loan_overdue':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'loan_reminder':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'equipment_fault':
        return <XCircle className="h-4 w-4 text-orange-500" />;
      case 'equipment_lost':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'announcement':
        return <Bell className="h-4 w-4 text-purple-500" />;
      default:
        return <Package className="h-4 w-4 text-gray-500" />;
    }
  };

  // v5.3.7: snooze actions
  const applySnooze = async (hours) => {
    try {
      const { data } = await api().post('/users/me/notification-snooze', {
        hours,
        types: hours > 0 ? snoozeTypeFilter : [],
      });
      setSnooze({
        active: data.active,
        snooze_until: data.snooze_until,
        snooze_types: data.snooze_types || [],
        digest_opt_in: snooze.digest_opt_in,
      });
      if (hours === 0) {
        toast.success('Snooze cancelado — recibirás notificaciones de nuevo');
      } else if (snoozeTypeFilter.length) {
        toast.success(`Pausadas ${snoozeTypeFilter.length} tipo(s) por ${hours}h`);
      } else {
        toast.success(`Todas las notificaciones pausadas por ${hours}h`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar snooze');
    }
  };

  // v5.3.8: per-user digest override
  const setDigestPreference = async (opt_in) => {
    try {
      const { data } = await api().post('/users/me/digest-preference', { opt_in });
      setSnooze(prev => ({ ...prev, digest_opt_in: data.digest_opt_in }));
      const label = opt_in === true ? 'digest diario' : opt_in === false ? 'entrega instantánea' : 'según config global';
      toast.success(`Preferencia: ${label}`);
    } catch (err) {
      toast.error('No se pudo guardar preferencia');
    }
  };

  // Available snooze types (with friendly labels)
  const SNOOZE_TYPES = [
    { id: 'movement_transition', label: 'Transiciones de movimientos' },
    { id: 'bomberazo',           label: 'Bomberazos' },
    { id: 'loan_overdue',        label: 'Préstamos vencidos' },
    { id: 'loan_reminder',       label: 'Recordatorios de préstamo' },
    { id: 'equipment_fault',     label: 'Equipos con fallas' },
    { id: 'equipment_lost',      label: 'Equipos perdidos' },
    { id: 'equipment_lost_overdue', label: 'Pérdidas vencidas' },
    { id: 'maintenance_overdue', label: 'Mantenimiento vencido' },
    { id: 'announcement',        label: 'Anuncios' },
  ];

  // v5.3.9: defensive HTML strip — older notifications stored as HTML show
  // raw tags otherwise. We strip a minimal allowlist of tags safely without
  // pulling in a heavyweight sanitizer.
  const stripHtml = (s) => {
    if (!s || typeof s !== 'string') return s;
    if (!/[<&]/.test(s)) return s;
    return s
      .replace(/<\/?(b|i|code|strong|em|a|br|p|div|span)[^>]*>/gi, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'normal': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white border-0"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[420px] sm:w-[480px] max-w-[90vw]" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm flex items-center gap-2">
            Notificaciones
            {snooze.active && (
              <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0" data-testid="snooze-active-badge">
                <BellOff className="h-3 w-3" /> Snooze
              </Badge>
            )}
          </span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7"
              onClick={markAllAsRead}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Marcar todas leídas'}
            </Button>
          )}
        </div>

        {/* v5.3.7/8: Snooze + digest preference controls */}
        <div className="px-3 py-2 border-b bg-muted/30" data-testid="snooze-bar">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <BellOff className="h-3 w-3" />
              Pausar por
            </span>
            <div className="flex items-center gap-1">
              {SNOOZE_PRESETS.map(p => (
                <Button
                  key={p.hours}
                  size="sm"
                  variant="outline"
                  className="h-6 px-1.5 text-[10px]"
                  data-testid={`snooze-preset-${p.hours}h`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); applySnooze(p.hours); }}
                >
                  {p.label}
                </Button>
              ))}
              {snooze.active && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-[10px] text-red-400"
                  data-testid="snooze-clear"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); applySnooze(0); }}
                >
                  Cancelar
                </Button>
              )}
            </div>
          </div>
          {snooze.active && snooze.snooze_until && (
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="snooze-until-label">
              Hasta {new Date(snooze.snooze_until).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
              {snooze.snooze_types?.length > 0 && (
                <> · solo {snooze.snooze_types.length} tipo(s)</>
              )}
            </p>
          )}

          {/* Granular type selector (v5.3.8) */}
          <details className="mt-1" data-testid="snooze-granular-details">
            <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
              Silenciar solo tipos específicos {snoozeTypeFilter.length > 0 && `(${snoozeTypeFilter.length})`}
            </summary>
            <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
              {SNOOZE_TYPES.map(t => (
                <label key={t.id} className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:bg-accent rounded px-1 py-0.5"
                       data-testid={`snooze-type-${t.id}`}>
                  <input
                    type="checkbox"
                    checked={snoozeTypeFilter.includes(t.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSnoozeTypeFilter(prev =>
                        prev.includes(t.id)
                          ? prev.filter(x => x !== t.id)
                          : [...prev, t.id]
                      );
                    }}
                    className="rounded h-3 w-3"
                  />
                  {t.label}
                </label>
              ))}
              {snoozeTypeFilter.length > 0 && (
                <p className="text-[10px] text-amber-400 mt-1 italic">
                  Aplicando snooze a {snoozeTypeFilter.length} tipo(s). Vacío = TODOS los tipos.
                </p>
              )}
            </div>
          </details>

          {/* Digest opt-in/out (v5.3.8) */}
          <div className="mt-2 pt-2 border-t border-border/40" data-testid="digest-pref-bar">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">Correos:</span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={snooze.digest_opt_in === false ? 'default' : 'outline'}
                  className="h-6 px-1.5 text-[10px]"
                  data-testid="digest-pref-instant"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDigestPreference(false); }}
                >
                  Instantáneos
                </Button>
                <Button
                  size="sm"
                  variant={snooze.digest_opt_in === true ? 'default' : 'outline'}
                  className="h-6 px-1.5 text-[10px]"
                  data-testid="digest-pref-digest"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDigestPreference(true); }}
                >
                  Digest
                </Button>
                <Button
                  size="sm"
                  variant={snooze.digest_opt_in === null || snooze.digest_opt_in === undefined ? 'default' : 'outline'}
                  className="h-6 px-1.5 text-[10px]"
                  data-testid="digest-pref-global"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDigestPreference(null); }}
                >
                  Global
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <span className="text-sm">No hay notificaciones</span>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                data-testid={`notification-item-${notification.id}`}
                className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${
                  !notification.is_read ? 'bg-primary/5' : ''
                }`}
                onClick={() => {
                  if (!notification.is_read) markAsRead(notification.id);
                  // v5.4.30 — Deep-link to the most relevant destination based on notification type/entity
                  const t = notification.type;
                  const movementId = notification.movement_id;
                  const equipmentId = notification.equipment_id;
                  const title = (notification.title || '').toLowerCase();
                  const message = (notification.message || '').toLowerCase();
                  let target = null;

                  if (t === 'movement_confirmation' && movementId) {
                    target = `/movements?confirm=${movementId}`;
                  } else if (movementId) {
                    target = `/movements?open=${movementId}`;
                  } else if (t === 'bomberazo' || title.includes('bomberazo')) {
                    target = '/audit?tab=bomberazos';
                  } else if (
                    t === 'equipment_lost' || t === 'equipment_lost_overdue' ||
                    title.includes('perdido') || message.includes('perdido')
                  ) {
                    target = '/equipment-tracking';
                  } else if (
                    t === 'loan_overdue' || t === 'loan_reminder' ||
                    title.includes('pendiente') || title.includes('retorno') ||
                    message.includes('sin retorno')
                  ) {
                    target = '/movements';
                  } else if (t === 'movement_transition') {
                    target = '/movements';
                  } else if (t === 'equipment_fault' && equipmentId) {
                    target = `/inventory?open=${equipmentId}`;
                  } else if (t === 'kpi_threshold_alert' || title.includes('kpi')) {
                    target = '/dashboard';
                  } else if (t === 'maintenance_overdue' || title.includes('mantenimiento')) {
                    target = '/maintenance';
                  } else if (t === 'announcement') {
                    target = '/announcements';
                  } else if (equipmentId) {
                    target = `/inventory?open=${equipmentId}`;
                  } else {
                    // Fallback: send the user to the audit log
                    target = '/audit';
                  }
                  window.location.href = target;
                }}
              >
                <div className="flex items-start gap-2 w-full">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type, notification.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {notification.title}
                      </span>
                      {!notification.is_read && (
                        <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {stripHtml(notification.message)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(notification.created_at)}
                      </span>
                      <Badge className={`text-xs ${getPriorityColor(notification.priority)}`}>
                        {notification.priority === 'urgent' ? 'Urgente' : 
                         notification.priority === 'high' ? 'Alta' : 
                         notification.priority === 'normal' ? 'Normal' : 'Baja'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                className="w-full text-sm"
                onClick={() => window.location.href = '/audit?tab=activity'}
              >
                Ver todas las notificaciones
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
